import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@skillarea/lib-frontend';
import {
  BOARD_SIZE,
  TicTacToeGame as TicTacToeEngine,
  type TBoard,
  type TCellValue,
  type TPlayer,
} from '@skillarea/game-tictactoe';

import type { TGamePlayer } from '../types';

import { TicTacToeGame, type ITicTacToeGameResult } from './TicTacToeGame';
import styles from './TicTacToeGameLocal.module.scss';

import { playLoseSound, playWinSound } from '~/lib/game-sounds';
import { useUserAvatar } from '~/stores/userStore';

interface ITicTacToeGameLocalProps {
  player1: TGamePlayer;
  player2: TGamePlayer;
  turnDurationMs?: number;
  enableAI?: boolean;
  aiDifficulty?: number;
  rules: string;
  showTestSituationButtons?: boolean;
  onGameEnd?: (result: ITicTacToeGameResult) => void;
  onSurrender?: () => void;
}

const DEFAULT_TURN_DURATION_MS = 5 * 60 * 1000;

type TTicTacToeBackendBoard = (null | 0 | 1)[][];
type TTicTacToeBotAction = { row: number; col: number };

const X = 0 as const;
const O = 1 as const;

function parseTicTacToeBoardJson(
  text: string,
): { board: TBoard } | { error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    return { error: 'Invalid JSON' };
  }

  if (!Array.isArray(parsed)) {
    return { error: 'Expected an array of rows (board)' };
  }

  if (parsed.length !== BOARD_SIZE) {
    return { error: `Expected board size ${BOARD_SIZE}×${BOARD_SIZE}` };
  }

  const board: TCellValue[][] = [];
  for (let row = 0; row < parsed.length; row += 1) {
    const rowValue = parsed[row];
    if (!Array.isArray(rowValue)) {
      return { error: `Row ${row} must be an array` };
    }
    if (rowValue.length !== BOARD_SIZE) {
      return { error: `Row ${row} must have length ${BOARD_SIZE}` };
    }

    const rowCells: TCellValue[] = [];
    for (let col = 0; col < rowValue.length; col += 1) {
      const cell = rowValue[col] as unknown;
      if (cell !== null && cell !== 0 && cell !== 1) {
        return { error: `Cell [${row}][${col}] must be null/0/1` };
      }
      rowCells.push(cell as TCellValue);
    }
    board.push(rowCells);
  }

  return { board };
}

function countMoves(board: (TPlayer | null)[][]): number {
  let moves = 0;
  for (const row of board) {
    for (const cell of row) {
      if (cell !== null) moves += 1;
    }
  }
  return moves;
}

async function fetchTicTacToeBotMove(params: {
  board: (TPlayer | null)[][];
  currentPlayer: TPlayer;
  difficulty01: number;
  clientSessionId: string;
}): Promise<TTicTacToeBotAction> {
  const response = await window.fetch('/api/game/bot-move', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      gameSlug: 'tic-tac-toe',
      state: {
        board: params.board as TTicTacToeBackendBoard,
        currentPlayer: params.currentPlayer,
        moves: countMoves(params.board),
        clientSessionId: params.clientSessionId,
      },
      playerSlot: 1,
      difficulty01: params.difficulty01,
    }),
  });

  if (!response.ok) {
    throw new Error(`Bot move request failed: ${response.status}`);
  }

  const data = (await response.json()) as { action: TTicTacToeBotAction };
  return data.action;
}

export const TicTacToeGameLocal: FC<ITicTacToeGameLocalProps> = ({
  aiDifficulty = 0.5,
  enableAI = true,
  onGameEnd,
  onSurrender,
  player1,
  player2,
  rules,
  showTestSituationButtons = false,
  turnDurationMs = DEFAULT_TURN_DURATION_MS,
}) => {
  const avatarUrl = useUserAvatar();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentPlayerRef = useRef<TPlayer>(X);
  const engineRef = useRef<TicTacToeEngine | null>(null);
  const playedResultRef = useRef(false);
  const botRequestIdRef = useRef(0);
  const clientSessionIdRef = useRef<string>(globalThis.crypto.randomUUID());

  const [board, setBoard] = useState<(TPlayer | null)[][]>([]);
  const [currentPlayer, setCurrentPlayer] = useState<TPlayer>(X);
  const [xTimeLeftMs, setXTimeLeftMs] = useState(turnDurationMs);
  const [oTimeLeftMs, setOTimeLeftMs] = useState(turnDurationMs);
  const [isGameOver, setIsGameOver] = useState(false);
  const [winningLine, setWinningLine] = useState<
    { row: number; col: number }[] | undefined
  >();
  const [winner, setWinner] = useState<TPlayer | null>(null);
  const [isDraw, setIsDraw] = useState(false);
  const [boardJsonDraft, setBoardJsonDraft] = useState('');
  const [boardPreview, setBoardPreview] = useState<TBoard | null>(null);
  const [boardJsonError, setBoardJsonError] = useState<string | null>(null);

  useEffect(() => {
    const mode = enableAI ? 'ai' : 'local';
    engineRef.current = new TicTacToeEngine(mode, aiDifficulty);
    engineRef.current.start();
    playedResultRef.current = false;
    botRequestIdRef.current += 1;
    clientSessionIdRef.current = globalThis.crypto.randomUUID();

    const state = engineRef.current.getState();
    setBoard(state.board);
    setCurrentPlayer(state.currentPlayer);
    setXTimeLeftMs(turnDurationMs);
    setOTimeLeftMs(turnDurationMs);

    setBoardPreview(null);
    setBoardJsonError(null);
  }, [enableAI, aiDifficulty, turnDurationMs]);

  currentPlayerRef.current = currentPlayer;

  useEffect(() => {
    if (isGameOver) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      const p = currentPlayerRef.current;
      if (p === X) {
        setXTimeLeftMs((prev) => Math.max(0, prev - 1000));
      } else {
        setOTimeLeftMs((prev) => Math.max(0, prev - 1000));
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isGameOver]);

  useEffect(() => {
    if (isGameOver) return;
    if (currentPlayer === X && xTimeLeftMs <= 0) {
      setIsGameOver(true);
      setWinner(O);
      setWinningLine(undefined);
      if (!playedResultRef.current) {
        playedResultRef.current = true;
        playLoseSound();
      }
      onGameEnd?.({ winner: O, isDraw: false });
      return;
    }
    if (currentPlayer === O && oTimeLeftMs <= 0) {
      setIsGameOver(true);
      setWinner(X);
      setWinningLine(undefined);
      if (!playedResultRef.current) {
        playedResultRef.current = true;
        playWinSound();
      }
      onGameEnd?.({ winner: X, isDraw: false });
    }
  }, [xTimeLeftMs, oTimeLeftMs, currentPlayer, isGameOver, onGameEnd]);

  const syncStateFromEngine = useCallback(() => {
    if (!engineRef.current) return;

    const state = engineRef.current.getState();
    setBoard([...state.board.map((row) => [...row])]);
    setCurrentPlayer(state.currentPlayer);

    if (state.status === 'finished') {
      setIsGameOver(true);
      setWinner(state.winner ?? null);
      setWinningLine(state.winningLine ?? undefined);
      if (!playedResultRef.current && state.winner !== null) {
        playedResultRef.current = true;
        if (state.winner === X) playWinSound();
        else playLoseSound();
      }
      onGameEnd?.({
        winner: state.winner,
        isDraw: false,
      });
    } else if (state.status === 'draw') {
      setIsGameOver(true);
      setIsDraw(true);
      onGameEnd?.({ winner: null, isDraw: true });
    }
  }, [onGameEnd]);

  const requestBackendBotMove = useCallback(async () => {
    if (!engineRef.current) return;
    const requestId = ++botRequestIdRef.current;

    try {
      const state = engineRef.current.getState();
      const action = await fetchTicTacToeBotMove({
        board: state.board,
        currentPlayer: state.currentPlayer,
        difficulty01: aiDifficulty,
        clientSessionId: clientSessionIdRef.current,
      });

      if (requestId !== botRequestIdRef.current || !engineRef.current) {
        return;
      }

      const result = engineRef.current.makeMove(O, action.row, action.col);
      if (result.success) {
        syncStateFromEngine();
      }
    } catch (error) {
      console.error('[TicTacToeLocalGame] Bot move failed:', error);
    }
  }, [aiDifficulty, syncStateFromEngine]);

  const handleCellClick = useCallback(
    (row: number, col: number) => {
      if (!engineRef.current || isGameOver) return;

      const state = engineRef.current.getState();
      if (state.currentPlayer !== X) return;

      const result = engineRef.current.makeMove(X, row, col);

      if (result.success) {
        syncStateFromEngine();

        if (!result.isGameOver && enableAI) {
          setTimeout(() => {
            if (!engineRef.current) return;
            void requestBackendBotMove();
          }, 500);
        }
      }
    },
    [isGameOver, syncStateFromEngine, enableAI, requestBackendBotMove],
  );

  const handleGameEnd = useCallback(
    (result: ITicTacToeGameResult) => {
      setIsGameOver(true);
      onGameEnd?.(result);
    },
    [onGameEnd],
  );

  const handleSurrender = useCallback(() => {
    setIsGameOver(true);
    if (!playedResultRef.current) {
      playedResultRef.current = true;
      playLoseSound();
    }
    onSurrender?.();
  }, [onSurrender]);

  const isMyTurn = currentPlayer === X && !isGameOver;

  const displayBoard = boardPreview ?? board;
  const displayCurrentPlayer = boardPreview ? X : currentPlayer;
  const displayIsGameOver = boardPreview ? false : isGameOver;
  const displayIsDraw = boardPreview ? false : isDraw;
  const displayWinner = boardPreview ? undefined : winner;
  const displayWinningLine = boardPreview ? undefined : winningLine;
  const displayIsMyTurn = boardPreview ? false : isMyTurn;
  const displayOnCellClick = boardPreview ? undefined : handleCellClick;

  const boardPreviewLabel = useMemo(() => {
    if (!boardPreview) return 'Board JSON (5×5, null/0/1)';
    return 'Board JSON (preview enabled)';
  }, [boardPreview]);

  const content = (
    <TicTacToeGame
      board={displayBoard}
      currentPlayer={displayCurrentPlayer}
      isDraw={displayIsDraw}
      isGameOver={displayIsGameOver}
      isMyTurn={displayIsMyTurn}
      onCellClick={displayOnCellClick}
      onGameEnd={handleGameEnd}
      onSurrender={handleSurrender}
      player1={{ ...player1, avatarUrl: player1.avatarUrl ?? avatarUrl }}
      player1TimeLeftMs={xTimeLeftMs}
      player2={player2}
      player2TimeLeftMs={oTimeLeftMs}
      playerSymbol={X}
      rulesContent={rules}
      turnDurationMs={turnDurationMs}
      winner={displayWinner}
      winningLine={displayWinningLine}
    />
  );

  if (!showTestSituationButtons) {
    return content;
  }

  return (
    <div className={styles.localRoot}>
      <div className={styles.boardInput}>
        <div className={styles.boardInputLabel}>{boardPreviewLabel}</div>
        <div className={styles.boardControlsRow}>
          <textarea
            className={styles.boardTextarea}
            onChange={(e) => setBoardJsonDraft(e.target.value)}
            placeholder={'[\n  [null, null, 0, null, null],\n  ...\n]'}
            rows={6}
            value={boardJsonDraft}
          />
          <div className={styles.boardButtons}>
            <Button
              className={styles.boardButton}
              color="accent"
              onClick={() => {
                const parsed = parseTicTacToeBoardJson(boardJsonDraft);
                if ('error' in parsed) {
                  setBoardJsonError(parsed.error);
                  return;
                }
                setBoardJsonError(null);
                setBoardPreview(parsed.board);
              }}
              size="s"
              type="button"
              variant="filled"
            >
              Apply
            </Button>
            <Button
              className={styles.boardButton}
              color="secondary"
              onClick={() => {
                setBoardPreview(null);
                setBoardJsonError(null);
              }}
              size="s"
              type="button"
              variant="outline"
            >
              Clear
            </Button>
          </div>
        </div>
        {boardJsonError ? (
          <div className={styles.boardInputError}>{boardJsonError}</div>
        ) : null}
      </div>
      {content}
    </div>
  );
};
